import { isValid, parseISO } from 'date-fns';
import React, { useContext, useEffect, useState, useCallback } from 'react';

import { INVITE_GUEST_PEER } from 'src/components/pages/eventVc.gql';
import VideoConference, {
  LegacyVideoConference,
} from 'src/components/pages/pageElements/videoConference';
import { useMarkProviderInVideoCall } from 'src/components/pages/pageElements/videoConference/useMarkProviderInVideoCall';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { USE_LEGACY_VIDEO_CONFERENCE } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import logger from 'src/shared/util/logger';
import type { EventInstance } from 'src/stores/models/event';
import useRootStore from 'src/stores/useRootStore';

const EventVC = () => {
  const [event, setEvent] = useState<EventInstance | null>(null);
  const { apolloClient } = useContext(ApolloClientContext);
  const flagUseLegacyVideoConference = useFeatureFlag(USE_LEGACY_VIDEO_CONFERENCE);
  const rootStore = useRootStore();
  const [useLegacyVideoConference, setUseLegacyVideoConference] = useState(true);

  useMarkProviderInVideoCall();

  useEffect(() => {
    setUseLegacyVideoConference(flagUseLegacyVideoConference);
    // only set this value on mount to avoid interrupting active video calls if the ff is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadEvent = async (): Promise<void> => {
      const eventData = await rootStore.events.getEventInstance(
        rootStore.routerStore.routerState.params.event,
      );
      setEvent(eventData);
    };

    loadEvent();
  }, [rootStore.events, rootStore.routerStore.routerState.params.event]);

  const inviteGuestByEmail = useCallback(
    async (email: string): Promise<void> => {
      try {
        if (!event?.id) return;
        await rootStore.events.inviteEventGuestByEmail(email, event.id);
      } catch (e) {
        logger.warn('Failed to send email invitation: ', e);
        // Allow anyone calling this to properly handle the error as well.
        throw e;
      }
    },
    [rootStore.events, event?.id],
  );

  const invitePeerWithReason = useCallback(
    async (reason: string): Promise<void> => {
      try {
        if (!event?.id || !apolloClient) return;
        await apolloClient.mutate({
          mutation: INVITE_GUEST_PEER,
          variables: { eventId: event.id, reason },
        });
      } catch (e) {
        logger.warn('Failed to send peer invitation: ', e);
        // Allow anyone calling this to properly handle the error as well.
        throw e;
      }
    },
    [apolloClient, event?.id],
  );

  if (!event || !event.vc || !event.id || !event.start) {
    return null;
  }

  const startDateTime = parseISO(event.start as unknown as string);
  const startTime = isValid(startDateTime) ? startDateTime.valueOf() : undefined;
  const VideoConferenceComponent = useLegacyVideoConference
    ? LegacyVideoConference
    : VideoConference;

  return (
    // @ts-expect-error rootStore type mismatch between full RootStore and expected subset
    <VideoConferenceComponent
      attendees={event.attendees as any}
      enableSharing
      eventId={event.id}
      guestUrl={event.vc.guestUrl || ''}
      inviteGuestByEmail={inviteGuestByEmail}
      invitePeerWithReason={invitePeerWithReason}
      sessionId={event.vc.sessionId}
      startTime={startTime}
      token={event.vc.token}
    />
  );
};

export default EventVC;
